import React from 'react';
import { Icon } from '@/shared/icons';

export interface TokenInfo {
  expiresAt: number; // unix seconds; 0 ou ausente = nunca expira
  scopes: string[];
  isValid: boolean;
  missingScopes: string[];
}

interface TokenStatusAlertProps {
  info: TokenInfo;
  testIdPrefix: string;
  /** Token bruto, usado para gerar o link de extensão para token de longa duração */
  accessToken: string;
}

const SHORT_EXPIRATION_THRESHOLD_DAYS = 7;
const META_GRAPH_API_VERSION = 'v23.0';

function buildExtendTokenUrl(token: string): string {
  return `https://developers.facebook.com/tools/debug/accesstoken/?access_token=${encodeURIComponent(token)}&version=${META_GRAPH_API_VERSION}`;
}

function formatExpirationDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function daysUntil(unixSeconds: number): number {
  const diffMs = unixSeconds * 1000 - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export const TokenStatusAlert: React.FC<TokenStatusAlertProps> = ({ info, testIdPrefix, accessToken }) => {
  const hasMissingScopes = info.missingScopes.length > 0;
  const isPermanent = !info.expiresAt;
  const remainingDays = isPermanent ? Infinity : daysUntil(info.expiresAt);
  const isExpiringSoon = !isPermanent && remainingDays <= SHORT_EXPIRATION_THRESHOLD_DAYS;
  const isExpiredOrInvalid = !info.isValid || (!isPermanent && remainingDays <= 0);

  // Sem nada para alertar (token permanente + scopes ok) → silencioso
  if (isPermanent && !hasMissingScopes && info.isValid) return null;

  // Vermelho: token inválido/expirado, expira em ≤7 dias, ou faltam scopes
  const isCritical = isExpiredOrInvalid || isExpiringSoon || hasMissingScopes;
  const bg = isCritical ? 'var(--theme-error-background)' : 'var(--theme-warning-background)';
  const border = isCritical ? 'var(--theme-error-color)' : 'var(--theme-warning-color)';
  const fg = isCritical ? 'var(--theme-error-color)' : 'var(--theme-warning-color)';
  const iconName = isCritical ? 'alert-circle' : 'alert-triangle';
  const showExtendLink = !isPermanent; // Mostra link sempre que o token tem expiração

  return (
    <div
      className="p-3 rounded-lg flex items-start gap-2"
      style={{ backgroundColor: bg, border: `1px solid ${border}` }}
      data-testid={`${testIdPrefix}-token-status`}
    >
      <Icon name={iconName} size={18} className="flex-shrink-0 mt-0.5" style={{ color: fg }} />
      <div className="flex-1 min-w-0 space-y-1.5">
        {!info.isValid && (
          <p className="text-sm font-medium" style={{ color: fg }}>
            Token inválido ou revogado.
          </p>
        )}
        {info.isValid && !isPermanent && remainingDays <= 0 && (
          <p className="text-sm font-medium" style={{ color: fg }}>
            Token expirado em {formatExpirationDate(info.expiresAt)}.
          </p>
        )}
        {info.isValid && isExpiringSoon && remainingDays > 0 && (
          <p className="text-sm font-medium" style={{ color: fg }}>
            Token expira em {remainingDays} dia{remainingDays === 1 ? '' : 's'} ({formatExpirationDate(info.expiresAt)}).
            {' '}Gere um token de longa duração para evitar que a integração caia.
          </p>
        )}
        {info.isValid && !isPermanent && !isExpiringSoon && (
          <p className="text-sm font-medium" style={{ color: fg }}>
            Token válido até {formatExpirationDate(info.expiresAt)} ({remainingDays} dias).
          </p>
        )}
        {hasMissingScopes && (
          <p className="text-xs" style={{ color: fg }}>
            <strong>Permissões faltantes:</strong> {info.missingScopes.join(', ')}
          </p>
        )}
        {showExtendLink && accessToken.trim() && (
          <a
            href={buildExtendTokenUrl(accessToken.trim())}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium hover:underline mt-0.5"
            style={{ color: fg }}
            data-testid={`${testIdPrefix}-token-extend-link`}
          >
            Gerar token de acesso ilimitado
            <Icon name="external-link" size={12} />
          </a>
        )}
      </div>
    </div>
  );
};

TokenStatusAlert.displayName = 'TokenStatusAlert';
